/**
 * Compile Gate Summary Dashboard
 * 
 * Shows overall status, severity breakdown, top issues, and review progress.
 */

import React from 'react';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { DarkPanel } from '@/components/ui/DarkPanel';
import { Badge } from '@/components/ui/badge';

interface Violation {
  severity: string;
  rule_name: string;
  rule_id: string;
}

interface CompileGateSummaryProps {
  overallStatus: string;
  violations: Violation[];
  reviewedCount: number;
}

export const CompileGateSummary: React.FC<CompileGateSummaryProps> = ({
  overallStatus,
  violations,
  reviewedCount
}) => {
  const total = violations.length;
  
  // Count by severity
  const severityCounts = {
    critical: violations.filter(v => v.severity.toLowerCase() === 'critical').length,
    high: violations.filter(v => v.severity.toLowerCase() === 'high').length,
    medium: violations.filter(v => v.severity.toLowerCase() === 'medium').length,
    low: violations.filter(v => v.severity.toLowerCase() === 'low').length
  };

  // Top 3 rules by count
  const ruleCounts: Record<string, number> = {};
  violations.forEach(v => {
    ruleCounts[v.rule_name] = (ruleCounts[v.rule_name] || 0) + 1;
  });
  const topRules = Object.entries(ruleCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);

  const reviewProgress = total > 0 ? Math.round((reviewedCount / total) * 100) : 100;

  const getStatusIcon = () => {
    switch (overallStatus?.toLowerCase()) {
      case 'pass':
        return <CheckCircle className="h-6 w-6 text-nb-success" />;
      case 'fail':
        return <XCircle className="h-6 w-6 text-nb-danger" />;
      default:
        return <AlertTriangle className="h-6 w-6 text-nb-warning" />;
    }
  };

  return (
    <DarkPanel className="p-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Status & Total */}
        <div className="flex items-center gap-4">
          {getStatusIcon()}
          <div>
            <div className="text-lg font-semibold">
              Status: <span className="capitalize">{overallStatus || 'pending'}</span>
            </div>
            <div className="text-sm text-nb-text-muted">
              {total.toLocaleString()} total violation{total !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {/* Severity Breakdown */}
        <div className="flex items-center gap-3">
          {severityCounts.critical > 0 && (
            <div className="text-center">
              <div className="text-2xl font-bold text-nb-danger">{severityCounts.critical}</div>
              <div className="text-xs text-nb-text-muted">Critical</div>
            </div>
          )}
          {severityCounts.high > 0 && (
            <div className="text-center">
              <div className="text-2xl font-bold text-nb-warning">{severityCounts.high}</div>
              <div className="text-xs text-nb-text-muted">High</div>
            </div>
          )}
          {severityCounts.medium > 0 && (
            <div className="text-center">
              <div className="text-2xl font-bold text-nb-warning">{severityCounts.medium}</div>
              <div className="text-xs text-nb-text-muted">Medium</div>
            </div>
          )}
          {severityCounts.low > 0 && (
            <div className="text-center">
              <div className="text-2xl font-bold text-nb-text-muted">{severityCounts.low}</div>
              <div className="text-xs text-nb-text-muted">Low</div>
            </div>
          )}
        </div>

        {/* Review Progress */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-nb-text-muted">Review Progress</span>
            <span className="text-sm font-medium text-nb-text-primary">{reviewProgress}%</span>
          </div>
          <div className="w-full bg-nb-surface-2 rounded-full h-2">
            <div
              className="bg-nb-accent h-2 rounded-full transition-all"
              style={{ width: `${reviewProgress}%` }}
            />
          </div>
          <div className="text-xs text-nb-text-muted mt-1">
            {reviewedCount} of {total} reviewed
          </div>
        </div>
      </div>

      {/* Top Issues */}
      {topRules.length > 0 && (
        <div className="mt-6 pt-6 border-t border-nb-border-subtle">
          <div className="text-sm font-medium text-nb-text-muted mb-3">Top Issues</div>
          <div className="flex flex-wrap gap-3">
            {topRules.map(([ruleName, count]) => (
              <Badge key={ruleName} variant="outline" className="text-xs">
                {ruleName}: {count} violation{count !== 1 ? 's' : ''}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </DarkPanel>
  );
};
